import {
  getDefaultsPvSystem,
  getDefaultsTariffs,
  useSite,
} from "@inrange/building-manager-api-client";
import {
  PartialSiteAllowStringValues,
  RecursivePartial,
  SiteAllowStringValues,
  TimedTariffDays,
} from "@inrange/building-manager-api-client/models-site";
import { useEffect, useRef } from "react";

export const useSetDefaultsOnCountryChange = (
  site: SiteAllowStringValues,
  setSite: (site: RecursivePartial<PartialSiteAllowStringValues>) => void,
  countryCode: string,
  currencyCode: string
) => {
  const { fetchSiteValues } = useSite({
    app: "admin",
    enableNonEssentialQueries: true,
  });
  const siteRef = useRef(site);
  siteRef.current = site;
  useEffect(() => {
    if (!fetchSiteValues.data) {
      // Site values not loaded yet
      return;
    }
    if (siteRef.current.currencyCode === currencyCode) {
      // Not change from initial site state
      return;
    }

    console.log(
      `Currency code changed from ${siteRef.current.currencyCode} to ${currencyCode}`
    );

    // Currency code has changed - reset to default values for the new country
    const defaultsPvSystem = getDefaultsPvSystem(
      fetchSiteValues.data!.options.pvPanels,
      fetchSiteValues.data!.options.pvSystems,
      currencyCode,
      siteRef.current.pvInverterBrand,
      siteRef.current.pvPanelName
    );
    const defaultsTariffs = getDefaultsTariffs(
      fetchSiteValues.data.options.tariffsDatas,
      currencyCode
    );
    const update = {
      currencyCode: currencyCode,
      costInputsPv: defaultsPvSystem.costInputsPv,
      tariffConfig: {
        onsitePPA: {
          tariff: defaultsTariffs.tenantTariff,
        },
        spillExportPPA: {
          tariff: defaultsTariffs.inrangeExportTariff,
        },
        supplier: {
          peakOffPeak: {
            overrideRatesMap: {
              peak: {
                tariff: defaultsTariffs.marketTariff,
              },
            },
          },
        },
      },
      battery: {},
    };
    for (const [batIdx, _bat] of Object.entries(
      siteRef.current.battery || {}
    )) {
      update.battery![batIdx] = {
        batteryEnergyFlowPayloadHash: null as unknown as undefined,
        batteryEnergyFlowFileVersionId: null as unknown as undefined,
        batterySpec: null as unknown as undefined,
      };
    }
    setSite(update);
  }, [setSite, fetchSiteValues.data, currencyCode]);

  useEffect(() => {
    if (siteRef.current.countryCode === countryCode) {
      // Not change from initial site state
      return;
    }

    // Country has changed
    if (countryCode === "GBR") {
      setSite({
        tariffConfig: {
          supplier: {
            peakOffPeak: {
              overrideRatesMap: {
                peak: {
                  rangesMap: {
                    "0": {
                      days: TimedTariffDays.EVERY_DAY,
                      startIndex: 14,
                      endIndex: 45,
                    },
                  },
                },
              },
            },
          },
        },
      });
    } else if (countryCode === "NLD") {
      setSite({
        tariffConfig: {
          supplier: {
            peakOffPeak: {
              overrideRatesMap: {
                peak: {
                  rangesMap: {
                    "0": {
                      days: TimedTariffDays.EVERY_DAY,
                      startIndex: 14,
                      endIndex: 46,
                    },
                  },
                },
              },
            },
          },
        },
      });
    }
  }, [setSite, countryCode]);
};
